import { ChangeDetectorRef, Component, effect, inject, OnDestroy, OnInit, signal } from "@angular/core";
import { BetPlacementService } from "../../../../../../shared/src/lib/services/bet-placement.service";
import { ApiService } from "../../../../../../shared/src";
import { combineLatestWith, Observable, Subject } from "rxjs";
import { ReactiveFormsModule } from "@angular/forms";
import { AsyncPipe, JsonPipe } from "@angular/common";
import { MatIconModule } from "@angular/material/icon";
import { MatButtonModule } from "@angular/material/button";
import { MarketService } from "../../../../../../shared/src/lib/services/market.service";
import { MatchedBetsComponent } from "./matched-bets/matched-bets.component";
import { UnmatchedBetsComponent } from "./unmatched-bets/unmatched-bets.component";
import { LiveUpdateService } from "../../../../../../shared/src/lib/services/live-update.service";
import { safeParseFloat } from "../../../../../../shared/src/lib/utils/common";
import { LoaderService } from "../../../../../../shared/src/lib/services/loader.service";
import { CoreService } from "../../../../../../shared/src/lib/services/core.service";
import { NavigationEnd, Router } from "@angular/router";
import { MatTooltip } from "@angular/material/tooltip";
import { SnackbarService } from "../../../../../../shared/src/lib/services/snackbar.service";
import { FeatureFlagDirective } from "../../../../../../shared/src/lib/directives/feature-flag.directive";
import { MarketOpsService } from "../../content/market-layout/market/market-ops.service";
import { MatAccordion, MatExpansionPanel, MatExpansionPanelHeader, MatExpansionPanelTitle } from "@angular/material/expansion";
import { ApiCallDurationComponent } from "../../../components/api-call-duration/api-call-duration.component";
import { LiveTvComponent } from "../live-tv/live-tv.component";
import { FeatureFlagService } from "../../../../../../shared/src/lib/services/feature-flag.service";
import { AlertComponent } from "../../../components/alert/alert.component";
import { Bet } from "../../../../../../shared/src/lib/types/bet.type";
import { MatDialog } from "@angular/material/dialog";
import { ConfirmationComponent } from "../../../components/confirmation/confirmation.component";
import { BalanceService } from "../../../../../../shared/src/lib/services/balance.service";
import { environment } from "../../../../../../shared/src/environments/environment";
import { takeUntil } from "rxjs/operators";

@Component({
  selector: "app-open-bets",
  imports: [
    MatIconModule,
    MatButtonModule,
    ReactiveFormsModule,
    MatchedBetsComponent,
    UnmatchedBetsComponent,
    AsyncPipe,
    MatTooltip,
    FeatureFlagDirective,
    MatAccordion,
    MatExpansionPanel,
    MatExpansionPanelHeader,
    MatExpansionPanelTitle,
    ApiCallDurationComponent,
    LiveTvComponent,
    AlertComponent,
    JsonPipe,
  ],
  templateUrl: "./open-bets.component.html",
  styleUrl: "./open-bets.component.scss",
})
export class OpenBetsComponent implements OnInit, OnDestroy {
  apiService = inject(ApiService);
  coreService = inject(CoreService);
  betPlacementService = inject(BetPlacementService);
  liveUpdateService = inject(LiveUpdateService);
  marketService = inject(MarketService);
  loader = inject(LoaderService);
  $loading!: Observable<boolean>;
  router = inject(Router);
  cdr = inject(ChangeDetectorRef);
  snackbar = inject(SnackbarService);
  marketOps = inject(MarketOpsService);
  currentParams: any = null;
  expandMatchOdds = true;
  expandAsianHandicap = true;
  expandBookmaker = true;
  expandFancy = true;
  featureFlagService = inject(FeatureFlagService);
  balanceService = inject(BalanceService);
  readonly dialog = inject(MatDialog);
  readonly destroy$ = new Subject<void>();

  matchOddsBets = signal<Bet[]>([]);
  unmatchedBets = signal<Bet[]>([]);
  lapsedBets = signal<Bet[]>([]);
  bookmakerBets = signal<Bet[]>([]);
  fancyBets = signal<Bet[]>([]);
  asianHandicapBets = signal<Bet[]>([]);
  openBets = signal<Bet[]>([]);
  openBetsBackup = signal<Bet[]>([]);

  constructor() {
    effect(() => {
      const data = this.liveUpdateService._getData(["bet_status"]);
      if (!data) return;
      console.log("Open bets status update ➡️ ", data);
      this.handleStatusUpdate(data);
    });
    this.betPlacementService.setRefresh(true);
  }

  get isDevOrUat() {
    return /(dev-|uat-)/.test(environment.sURL);
  }

  ngOnInit() {
    this.filterBets(this.router.url);
    this.router.events.pipe(takeUntil(this.destroy$)).subscribe((event: any) => {
      if (event instanceof NavigationEnd) {
        this.filterBets(event?.url);
      }
    });
    this.$loading = this.loader.isLoading("/bf/current-user-order");
    this.getOpenBets();
    this.getPlacement();
  }

  getPlacement() {
    this.betPlacementService
      .getParams()
      .pipe(takeUntil(this.destroy$))
      .subscribe((params: any) => {
        this.currentParams = params;
        if (params) {
          switch (params?.data?.context) {
            case "match-odds":
              this.expandMatchOdds = true;
              break;
            case "bookmaker":
              this.expandBookmaker = true;
              break;
            case "fancy":
              this.expandFancy = true;
              break;
          }
        }
      });
  }

  filterBets(url: string) {
    const eventTypeMappings: Record<string, string> = {
      "content:soccer": "1",
      "highlights/soccer": "1",
      "content:tennis": "2",
      "highlights/tennis": "2",
      "content:cricket": "4",
      "highlights/cricket": "4",
      "content:horse": "7",
      "content:greyhound": "4339",
    };

    this.marketOps
      .getTopics()
      .pipe(combineLatestWith(this.marketOps.allMarkets$), takeUntil(this.destroy$))
      .subscribe(([topics, markets]) => {
        const marketIds = markets?.map((market: any) => market?.topic ?? market?.marketId);
        const marketId = url.match(/\/market\/([^/]+)(?:\/|$)/)?.[1];
        const eventId = url.match(/event-markets\/(\d+)(?=[^\d])/)?.[1];
        const filterUtil = (field: string, value: any) => {
          return this.openBetsBackup().filter((item: any) => {
            if (field === "marketId") return topics.includes(item[field]);
            if (field === "eventId" && marketIds?.length) return marketIds.includes(item["topic"] ?? item["marketId"]);
            return item[field] === value;
          });
        };
        const matchedEventType = Object.keys(eventTypeMappings).find((key) => url.includes(key));

        if (matchedEventType) {
          this.openBets.set(filterUtil("eventTypeId", eventTypeMappings[matchedEventType]));
        } else if (url.includes("/market/")) {
          this.openBets.set(filterUtil("marketId", marketId));
        } else if (url.includes("content:event-markets")) {
          this.openBets.set(filterUtil("eventId", eventId));
        } else {
          this.openBets.set(this.openBetsBackup());
        }

        this.handleBetSegregation(this.openBets());
      });
  }

  handleStatusUpdate(update: any) {
    const authToken = this.apiService.getUserToken();
    const userId = authToken?.user?.ID;
    const data = update?.data;
    if (data && userId === data?.user_id) {
      // console.log(data);
      this.betPlacementService.setRefresh(true);
    }
  }

  getOpenBets() {
    this.betPlacementService.openBetsSubject.pipe(takeUntil(this.destroy$)).subscribe((data) => {
      if (data) {
        const nonPendingBets = data?.filter((item: any) => item?.status !== "PENDING");
        this.betPlacementService.getMarketsForACHBets(nonPendingBets);
        this.openBets.set(nonPendingBets);
        this.openBetsBackup.set(this.openBets());
        this.filterBets(this.router.url);
        this.handleBetSegregation(this.openBets());
      }
    });
  }

  handleBetSegregation(data: any) {
    let matched = data?.filter(
      (bet: any) => safeParseFloat(bet?.stakeRemaining) === 0 && safeParseFloat(bet?.stakeMatched) > 0 && safeParseFloat(bet?.averagePriceMatched) > 0,
    );
    let unmatched = data?.filter((bet: any) => {
      return safeParseFloat(bet?.stakeRemaining) > 0 && safeParseFloat(bet?.stakeMatched) === 0 && safeParseFloat(bet?.sizeLapsed) === 0;
    });
    const partiallyMatched = data?.filter((bet: any) => {
      return safeParseFloat(bet?.stakeRemaining) > 0 && safeParseFloat(bet?.stakeMatched) > 0 && safeParseFloat(bet?.sizeLapsed) === 0;
    });
    if (partiallyMatched?.length > 0) {
      console.log("%cPartially Matched Bets", "color: yellow; font-weight: bold;", partiallyMatched);
      partiallyMatched.forEach((bet: any) => {
        const profit = (bet?.priceSize?.price - 1) * bet?.stakeRemaining;
        const loss = bet?.stakeRemaining;
        const isBack = bet?.size === "BACK";
        const matchedBet = { ...bet, stakeRemaining: 0 };
        const unmatchedBet = {
          ...bet,
          stakeMatched: 0,
          potentialProfit: isBack ? profit : loss,
          potentialLoss: isBack ? loss : profit,
        };
        matched = [...matched, matchedBet];
        unmatched = [...unmatched, unmatchedBet];
      });
    }
    const lapsed = data?.filter((bet: any) => {
      return safeParseFloat(bet?.stakeRemaining) === 0 && safeParseFloat(bet?.stakeMatched) === 0 && safeParseFloat(bet?.sizeLapsed) > 0;
    });
    const matchOdds = matched?.filter((bet: any) => bet?.provider === "BF" && !bet?.bettingType?.includes("ASIAN_HANDICAP"));
    const bookmaker = matched?.filter((bet: any) => bet?.descriptionPrefix === "Bookmaker");
    const fancy = matched?.filter((bet: any) => bet?.descriptionPrefix === "Fancy");
    const asianHandicap = matched?.filter((bet: any) => bet?.bettingType?.includes("ASIAN_HANDICAP"));

    this.betPlacementService.setUnmatchedBets(unmatched?.map((bet: Bet) => bet?.betId));
    this.unmatchedBets.set(unmatched);
    this.lapsedBets.set(lapsed);
    this.matchOddsBets.set(matchOdds);
    this.bookmakerBets.set(bookmaker);
    this.fancyBets.set(fancy);
    this.asianHandicapBets.set(asianHandicap);
  }

  handleCopy() {
    const ids = this.openBets()
      .map((item) => [item?.customerOrderRef])
      ?.join(",");
    navigator.clipboard
      .writeText(ids)
      .then(() => {
        this.snackbar.openSnackbar({ message: "All IDs copied to clipboard!" });
      })
      .catch((err) => {
        console.error("Failed to copy IDs:", err);
      });
  }

  get userName() {
    const user = this.apiService.getUserToken();
    return user?.user?.NAME;
  }

  handleMultipleCancellation(e: any) {
    e.stopPropagation();
    const dialogRef = this.dialog.open(ConfirmationComponent, {
      data: {
        title: "Confirm Cancellation",
        message: "Are you sure you want to cancel all unmatched bets?",
      },
      exitAnimationDuration: 0,
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        if (result) this.betPlacementService.notifyChange("multipleCancellation");
      });
  }

  ngOnDestroy() {
    // console.log("I got called boy");
    this.destroy$.next();
    this.destroy$.complete();
  }
}
